"use client"

import {
  ButtonSurface,
  PrimaryButton,
  SecondaryButton,
} from "@/components/buttons"
import { ProseContainer } from "@/components/common"
import { useEffect, useState } from "react"
import Link from "next/link"
import { AnimatePresence, motion } from "framer-motion"
import Cookies from "js-cookie"
import { CONSENT_COOKIE } from "./constants"

declare global {
  interface Window {
    gtag: (
      command: "consent",
      action: "default" | "update",
      params: object
    ) => void
  }
}

/**
 * A cookie consent banner that slides in from the bottom of the screen.
 * It provides options for the user to accept or decline tracking cookies.
 * The component handles setting the consent cookie and signaling the user's
 * choice to Google Analytics.
 *
 * Cookie durations:
 * - Accepted: 180 days (6 months) - respects user's positive choice for longer
 * - Declined: 30 days - allows users to reconsider more frequently while respecting their privacy
 */
export function CookieBanner() {
  const [isVisible, setIsVisible] = useState(true)

  // Check if cookie already exists on mount
  useEffect(() => {
    const existingConsent = Cookies.get(CONSENT_COOKIE)
    if (existingConsent !== undefined) {
      setIsVisible(false)
    }
  }, [])

  const handleAccept = () => {
    window.gtag("consent", "update", {
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      analytics_storage: "granted",
    })

    // Set cookie without secure flag in development
    Cookies.set(CONSENT_COOKIE, "true", {
      expires: 180, // 6 months for accepted
      path: "/",
      sameSite: "lax",
      // Don't set secure flag at all in development
      ...(process.env.NODE_ENV === "production" && { secure: true }),
    })

    // Hide the banner immediately
    setIsVisible(false)
    // Force a hard refresh to ensure server components see the cookie
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }

  const handleDecline = () => {
    // Explicitly update consent state to denied
    window.gtag("consent", "update", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
    })

    // Set cookie without secure flag in development
    Cookies.set(CONSENT_COOKIE, "false", {
      expires: 30, // 30 days for declined
      path: "/",
      sameSite: "lax",
      // Don't set secure flag at all in development
      ...(process.env.NODE_ENV === "production" && { secure: true }),
    })

    // Hide the banner immediately
    setIsVisible(false)
    // Force a hard refresh to ensure server components see the cookie
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }

  if (!isVisible) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: "0%" }}
        exit={{ y: "100%" }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        className="fixed bottom-0 left-0 z-50 w-full"
      >
        <div className="mx-auto max-w-4xl px-4 pb-4">
          <div className="rounded-lg border border-gray-200 bg-white/80 p-6 shadow-2xl backdrop-blur-lg dark:border-neutral-700 dark:bg-neutral-800/80">
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <ProseContainer className="text-center">
                We use cookies to enhance your browsing experience and analyze
                our traffic.
                <br />
                By clicking &quot;Accept All&quot;, you consent to our use of
                cookies. Read our{" "}
                <Link
                  href="/legal/privacy-policy"
                  className="underline hover:text-text-primary"
                >
                  Privacy Policy
                </Link>
                .
              </ProseContainer>
              <div className="flex flex-shrink-0 gap-4">
                <PrimaryButton
                  buttonProps={{ onClick: handleAccept }}
                  surface={ButtonSurface.OnNeutral}
                >
                  Accept All
                </PrimaryButton>
                <SecondaryButton
                  buttonProps={{ onClick: handleDecline }}
                  surface={ButtonSurface.OnNeutral}
                >
                  Decline
                </SecondaryButton>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
